import { create } from "zustand";
import { persist } from "zustand/middleware";

interface DeviceState {
  selectedDeviceId: string;
  selectedDeviceName: string;
  selectedDeviceIds: string[];
  selectedDeviceNames: Record<string, string>;
  setSelectedDevice: (deviceId: string, deviceName: string) => void;
  setSelectedDevices: (devices: { id: string; name: string }[]) => void;
  toggleDevice: (deviceId: string, deviceName: string) => void;
  clearSelectedDevice: () => void;
}

export const useDeviceStore = create<DeviceState>()(
  persist(
    (set, get) => ({
      selectedDeviceId: "",
      selectedDeviceName: "",
      selectedDeviceIds: [],
      selectedDeviceNames: {},
      setSelectedDevice: (deviceId: string, deviceName: string) =>
        set({
          selectedDeviceId: deviceId,
          selectedDeviceName: deviceName,
          selectedDeviceIds: deviceId ? [deviceId] : [],
          selectedDeviceNames: deviceId ? { [deviceId]: deviceName } : {},
        }),
      setSelectedDevices: (devices: { id: string; name: string }[]) =>
        set({
          selectedDeviceIds: devices.map((d) => d.id),
          selectedDeviceNames: Object.fromEntries(
            devices.map((d) => [d.id, d.name]),
          ),
          // Primary device is always the first selected
          selectedDeviceId: devices[0]?.id || "",
          selectedDeviceName: devices[0]?.name || "",
        }),
      toggleDevice: (deviceId: string, deviceName: string) => {
        const { selectedDeviceIds, selectedDeviceNames } = get();
        const isSelected = selectedDeviceIds.includes(deviceId);

        let newIds: string[];
        let newNames: Record<string, string>;

        if (isSelected) {
          // Don't allow deselecting the last device
          if (selectedDeviceIds.length <= 1) return;
          newIds = selectedDeviceIds.filter((id) => id !== deviceId);
          newNames = { ...selectedDeviceNames };
          delete newNames[deviceId];
        } else {
          newIds = [...selectedDeviceIds, deviceId];
          newNames = { ...selectedDeviceNames, [deviceId]: deviceName };
        }

        set({
          selectedDeviceIds: newIds,
          selectedDeviceNames: newNames,
          // Primary device is always the first selected
          selectedDeviceId: newIds[0] || "",
          selectedDeviceName: newNames[newIds[0]] || "",
        });
      },
      clearSelectedDevice: () =>
        set({
          selectedDeviceId: "",
          selectedDeviceName: "",
          selectedDeviceIds: [],
          selectedDeviceNames: {},
        }),
    }),
    {
      name: "device-storage",
    },
  ),
);
